import React, { useCallback, useEffect, useMemo, useState, useRef, ReactElement } from 'react';
import arrowRight from '@sg/assests/svg/arrow-right-white.svg';
import './index.scss';
import { Button, Justify, Segment, Select, Table, DatePicker, notification } from 'tea-component';
import moment from 'moment';
import TableForm from '@sg/components/TableForm';
import DatePickerWrapper from '@gp/components/DatePickerWrapper';
import MKTCost from './MKTCost';
import Headcount from './Headcount';
import Project from './Project';
import Overview from './Overview';
import NoAuthPage from '@sg/components/noAuthPage';
import { useIntl, useLocation, history } from 'umi';
import { 
  SGBuriedPoint, 
  SGBuriedBtnPoint, 
  PublishingManagementConfig, 
  OverviewTab, 
  MKTCostTab, 
  PublishingHeadcountTab, 
  PublishingProjectTab,
  createBtnPoint,
  createPagePoint
} from '@sg/utils/buriedPoint';
import { getFormattedTabList } from '@sg/_config/permission';
import { usePermissionReady } from '@sg/hooks/usePermissionReady';
import { ITabListProp } from '../BusinessManagement/type';
import { getDataTime } from '@sg/services';
import { getPublishingModulesForPage, GLOBAL_MIN_DATE } from '@sg/_config/dataTimeModules';

const { MonthPicker } = DatePicker;

export default function PublishingManagement(props: any) {
  const { formatMessage } = useIntl();
  const { activeSubTab, setActiveSubTab } = props;
  const { ready: permissionReady } = usePermissionReady();
  const [tabList, setTabList] = useState<ITabListProp[]>([]);
  const [maxDataTime, setMaxDataTime] = useState<string | null>(null);

  // State to track each tab's selected month
  const [tabMonthSelections, setTabMonthSelections] = useState<Record<string, any>>({
    overview: null,
    mktCost: null,
    publishingHeadcount: null,
    publishingProject: null,
  });

  // State to track if any tab is in editing mode (disable date picker when editing)
  const [isEditing, setIsEditing] = useState(false);

  // State to track current project_id for Project page (used for fetchMaxDataTime)
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  
  // Tab埋点 config mapping
  const TAB_CONFIGS = {
    overview: OverviewTab,
    mktCost: MKTCostTab,
    publishingHeadcount: PublishingHeadcountTab,
    publishingProject: PublishingProjectTab,
  };
  
  // Read activeSegment from URL instead of state
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const activeSegment = searchParams.get('subtitle') || activeSubTab || 'overview';
  
  // Page埋点 - Track page view when sub-tab changes and reset editing state
  useEffect(() => {
    const tabConfig = TAB_CONFIGS[activeSegment as keyof typeof TAB_CONFIGS];
    if (tabConfig) {
      const pagePoint = createPagePoint(tabConfig);
      if (pagePoint) {
        SGBuriedPoint(pagePoint);
      }
    }

    // Reset editing state when switching tabs
    setIsEditing(false);
  }, [activeSegment]);

  // Fetch max data time when active segment changes or project_id changes
  useEffect(() => {
    const fetchMaxDataTime = async () => {
      const modules = getPublishingModulesForPage(activeSegment);
      if (modules.length === 0) return;

      // For Project page, wait until project_id is available before calling API
      if (activeSegment === 'publishingProject' && !currentProjectId) {
        return;
      }

      try {
        // Build payload with modules
        const payload: any = { modules };
        
        // Add project_id if on Project page and project is selected
        if (activeSegment === 'publishingProject' && currentProjectId) {
          payload.project_id = Number(currentProjectId);
        }
        
        const response = await getDataTime(payload);
        if (response.code === 0 && response.data?.max_datatime) {
          setMaxDataTime(response.data.max_datatime);
        } else {
          // API returned success but no max_datatime
          setMaxDataTime(null);
          // notification.error({
          //   description: 'Unable to retrieve the maximum available date. Please try again later.',
          // });
        }
      } catch (error) {
        console.error('Failed to fetch max data time:', error);
        setMaxDataTime(null);
        notification.error({
          description: 'An error occurred while fetching maximum available date. Please try again later.',
        });
      }
    };

    fetchMaxDataTime();
  }, [activeSegment, currentProjectId]);

  // Refs to child components → allow parent to call exposed methods
  const overviewRef = useRef<any>(null); // will expose scrollToGovernance()

  // useMemo ensures OPTIONS_SEGMENT updates when tabMonthSelections changes
  const OPTIONS_SEGMENT: ITabListProp[] = useMemo(() => {
    // Format the selected month to "YYYY-MM" string for API payload
    const formattedOverviewMonth = tabMonthSelections.overview 
      ? moment(tabMonthSelections.overview).format('YYYY-MM') 
      : null;
    
    const formattedMKTCostMonth = tabMonthSelections.mktCost
      ? moment(tabMonthSelections.mktCost).format('YYYY-MM')
      : null;
    
    const formattedHeadcountMonth = tabMonthSelections.publishingHeadcount
      ? moment(tabMonthSelections.publishingHeadcount).format('YYYY-MM')
      : null;
    
    const formattedProjectMonth = tabMonthSelections.publishingProject
      ? moment(tabMonthSelections.publishingProject).format('YYYY-MM')
      : null;
    
    return [
      {
        key: 'publishing_overview',
        text: formatMessage({ id: 'dbs_subtitle_overview' }),
        value: "overview",
        modules: (
          <Overview 
            key={formattedOverviewMonth || 'default'}
            ref={overviewRef} 
            selectedMonth={formattedOverviewMonth}
            maxDataTime={maxDataTime}
            onEditingChange={setIsEditing}
          />
        ),
        trackingKey: 'tabClick'
      },
      {
        key: 'publishing_mktcost',
        text: formatMessage({ id: 'dbs_subtitle_mktcost' }),
        value: "mktCost",
        modules: (
          <MKTCost 
            key={formattedMKTCostMonth || 'default'} 
            selectedMonth={formattedMKTCostMonth}
            maxDataTime={maxDataTime}
            onEditingChange={setIsEditing}
          />
        ),
        trackingKey: 'tabClick'
      },
      {
        key: 'publishing_headcount',
        text: formatMessage({ id: 'dbs_subtitle_publishing_headcount' }),
        value: "publishingHeadcount",
        modules: (
          <Headcount 
            key={formattedHeadcountMonth || 'default'} 
            selectedMonth={formattedHeadcountMonth}
            maxDataTime={maxDataTime}
            onEditingChange={setIsEditing}
          />
        ),
        trackingKey: 'tabClick'
      },
      {
        key: 'publishing_project',
        text: formatMessage({ id: 'dbs_subtitle_publishing_project' }),
        value: "publishingProject",
        modules: (
          <Project 
            key={formattedProjectMonth || 'default'} 
            selectedMonth={formattedProjectMonth} 
            maxDataTime={maxDataTime}
            onProjectChange={setCurrentProjectId} 
            onEditingChange={setIsEditing} 
            />
          ),
        trackingKey: 'tabClick'
      },
    ];
  }, [tabMonthSelections, maxDataTime, formatMessage]); // Re-create when month selections change


  const handleActiveSegmentChange = (value: string) => {
    // Update URL using history.push to enable RoutePrompt interception
    const currentParams = new URLSearchParams(location.search);
    const params = new URLSearchParams();
    const tab = currentParams.get('tab');
    if (tab) {
      params.set('tab', tab);
    }
    params.set('subtitle', value);
    history.push(`/strategic/home?${params.toString()}`);
    
    // Still call setActiveSubTab for backward compatibility with parent
    setActiveSubTab(value);
    
    // Button埋点 - Track tab click
    const data = tabList.find(item => item.value === value);
    if (data?.trackingKey) {
      const tabConfig = TAB_CONFIGS[value as keyof typeof TAB_CONFIGS];
      if (tabConfig) {
        const btnPoint = createBtnPoint(tabConfig, data.trackingKey, data.text);
        if (btnPoint) {
          SGBuriedBtnPoint(btnPoint);
        }
      }
    }
  };


  // Handle date selection for the current tab
  const handleDateChange = (date: any) => {
    setTabMonthSelections(prev => ({
      ...prev,
      [activeSegment]: date
    }));

    const tabConfig = TAB_CONFIGS[activeSegment as keyof typeof TAB_CONFIGS];
    if (!tabConfig) {
      return;
    }

    const selectedValue = date ? moment(date).format('YYYY-MM') : 'Cleared';
    let extInfo = `Month Picker - ${selectedValue}`;

    if (activeSegment === 'publishingProject') {
      extInfo = `${extInfo} - Project ID: ${currentProjectId || 'Unknown'}`;
    }

    const btnPoint = createBtnPoint(
      tabConfig,
      'monthPicker',
      extInfo,
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
  };

  // Disable months outside October 2025 - max data time from API
  // Return false for disabled dates, true for enabled dates
  const disabledMonth = (date: any) => {
    // If we don't have maxDataTime yet, disable all months
    if (!maxDataTime) {
      return false;
    }
    
    const minDate = moment(GLOBAL_MIN_DATE, 'YYYY-MM'); // 2025-10
    const maxDate = moment(maxDataTime, 'YYYY-MM');
    
    // Return true if date is within range (enabled), false otherwise (disabled)
    return !date.isBefore(minDate, 'month') && !date.isAfter(maxDate, 'month');
  };

  // Get the current tab's selected date
  const currentSelectedMonth = tabMonthSelections[activeSegment];

  // Date picker form configuration
  const dateFormList = [
    {
      key: 'date',
      render: () => (
        <DatePickerWrapper className='dbs-month-picker-with-tooltip' label={formatMessage({ id: 'dbs_month' })}>
          <MonthPicker
            overlayClassName='dbs-month-picker-overlay'
            value={currentSelectedMonth}
            onChange={handleDateChange}
            disabledMonth={disabledMonth}
            placeholder="Please Select"
            clearable
            disabled={isEditing}
          />
        </DatePickerWrapper>
      )
    }
  ];

  const init = () => {
    const newTab = getFormattedTabList(OPTIONS_SEGMENT);
    setTabList(newTab);
  };

  // 初始化
  useEffect(() => {
    init();
  }, [OPTIONS_SEGMENT, permissionReady]); // Re-run when OPTIONS_SEGMENT or permissions change

  const activeTabHasView = useMemo(
    () => tabList.find(item => item.value === activeSegment)?.hasAuth,
    [tabList, activeSegment],
  );

  return (
    <>
      <div className="dbs-segment-row">
        <div className="dbs-segment-row__left">
          <Segment
            className="dbs-gradient-segment"
            options={tabList}
            value={activeSegment}
            onChange={handleActiveSegmentChange}
          />
        
          {/* Date Picker */}
          {activeTabHasView && <TableForm formList={dateFormList} />}
        </div>

        {activeSegment === 'overview' && activeTabHasView && (
          // 1.0 Decision Points List button at Overview tab
          <Button
            className="dbs-gradient-btn"
            onClick={() => {
              // Track buried point for Decision Point List button
              const btnPoint = createBtnPoint(
                OverviewTab,
                'decisionPointList',
                'Decision Point List'
              );
              if (btnPoint) {
                SGBuriedBtnPoint(btnPoint);
              }
              overviewRef.current?.scrollToDecisionPoints();
            }}
          >
            {formatMessage({ id: 'dbs_button_decision_point_list' })}
            <img src={arrowRight} alt="Arrow Right" />
          </Button>
        )}
      </div>
      <div className="dbs-publishing-management-content">
        {
          tabList.map(item => {
            if (activeSegment === item.value) {
              return item.hasAuth ? (
                // Added keys to all mapped elements:
                // React.cloneElement(item.modules as ReactElement, { key: item.key })
                
                // NOTE: Keep direct render here (do NOT use React.cloneElement with a fixed key).
                // Publishing Overview event bubbles can get stale coordinates on month switch when
                // component identity is forced stable; direct render preserves month-key remount behavior.
                item.modules
              ) : (
                <NoAuthPage key={item.key} admins={item.admins || []} />
              );
            }
            return null;
          })
        }
      </div>
    </>
  );
}
